<template>
  <div class="rm-screen rm-gm">
    <div v-if="loading && !days.length" class="rm-empty">
      <v-progress-circular indeterminate color="#c2410c"></v-progress-circular>
    </div>

    <template v-else>
      <div v-if="orderCount" class="rm-stats">
        <div class="rm-stat rm-stat--inverse">
          <span class="rm-stat__label">{{ $t("total") }}</span>
          <span class="rm-stat__value rm-stat__value--small rm-num">{{ totalText }}</span>
        </div>
        <div class="rm-stat">
          <span class="rm-stat__label">{{ $t("orders") }}</span>
          <span class="rm-stat__value rm-num">{{ orderCount }}</span>
        </div>
        <div v-if="average" class="rm-stat rm-stat--accent">
          <span class="rm-stat__label">{{ $t("guest_desktop.average") }}</span>
          <span class="rm-stat__value rm-stat__value--small rm-num">{{ average }}</span>
        </div>
      </div>

      <article v-for="day in dayRows" :key="day.date" class="rm-card rm-gi-day">
        <button type="button" class="rm-gi-day__head" :aria-expanded="String(isOpen(day))" @click="toggle(day)">
          <span class="rm-gi-day__text">
            <b>{{ day.title }}</b>
            <span class="rm-muted rm-num">{{ pluralOrders(day.orders.length) }}</span>
          </span>
          <b class="rm-gi-day__amount rm-num">{{ day.amountText }}</b>
          <v-icon size="20" color="#8a817a">{{ isOpen(day) ? "mdi-chevron-up" : "mdi-chevron-down" }}</v-icon>
        </button>
        <div class="rm-progress rm-gi-day__bar" role="progressbar" :aria-valuenow="day.share" aria-valuemin="0" aria-valuemax="100">
          <div class="rm-progress__fill" :style="{ width: `${day.share}%` }"></div>
        </div>

        <ul v-if="isOpen(day)" class="rm-list rm-gi-orders">
          <li v-for="order in day.orders" :key="order.id">
            <button type="button" class="rm-gi-order" :aria-label="`${$t('order_details')} #${order.id}`" @click="open(order, $event)">
              <span class="rm-gi-order__main">
                <span class="rm-gi-order__line">
                  <b class="rm-num rm-gi-order__id">#{{ order.id }}</b>
                  <span class="rm-num">{{ order.time || "–" }}</span>
                </span>
                <span class="rm-gi-order__name">{{ order.displayName }}</span>
              </span>
              <span class="rm-gi-order__side">
                <b class="rm-num">{{ order.amountText }}</b>
                <span class="rm-go-badge rm-go-badge--static" :style="badgeStyle(order.state)">
                  <i class="rm-go__dot" :style="dotStyle(order.state)"></i>
                  {{ $t(order.state) }}
                </span>
              </span>
            </button>
          </li>
        </ul>
      </article>

      <div v-if="!dayRows.length" class="rm-empty">{{ emptyTitle }}</div>
    </template>
  </div>
</template>

<script>
import moment from "moment";
import hasGuestOrderStatus from "@/mixins/report/hasGuestOrderStatus";
import { orderTotal } from "@/utils/guestOrderTotal";
import { menuState } from "@/utils/guestOrderStatus";
import "./guestMobileLists.scss";

/**
 * Prihodi na telefonu: kartica po danu (iznos, traka prema najboljem danu) koja se širi u spisak
 * narudžbi; narudžba otvara detalje. Ista svojstva i događaji kao GuestIncomeView (računar).
 */
export default {
  name: "GuestMobileIncomeView",
  mixins: [hasGuestOrderStatus],
  props: {
    days: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false },
    search: { type: String, default: "" },
  },
  data: () => ({
    openDays: {},
  }),
  computed: {
    dayRows() {
      const rows = this.days
        .map((day) => {
          const orders = this.customSearch(
            day.orders.map((order) => this.toOrder(order)),
            [{ value: "displayName" }, { value: "phone" }, { value: "idText" }],
            this.search
          );
          return { date: day.date, orders, parts: this.partsOf(orders) };
        })
        .filter((day) => day.orders.length);
      const max = Math.max(0, ...rows.map((day) => day.parts.reduce((sum, part) => sum + part.amount, 0)));
      return rows.map((day) => {
        const amount = day.parts.reduce((sum, part) => sum + part.amount, 0);
        return { ...day, title: this.dayTitle(day.date), amountText: this.partsText(day.parts), share: max ? Math.round((amount / max) * 100) : 0 };
      });
    },
    allOrders() {
      return this.dayRows.flatMap((day) => day.orders);
    },
    orderCount() {
      return this.allOrders.length;
    },
    totalParts() {
      return this.partsOf(this.allOrders);
    },
    totalText() {
      return this.partsText(this.totalParts);
    },
    average() {
      if (!this.orderCount || this.totalParts.length !== 1) {
        return "";
      }
      return `${(this.totalParts[0].amount / this.orderCount).toFixed(2)} ${this.totalParts[0].currency}`;
    },
    emptyTitle() {
      return this.search ? this.$t("report_mobile.no_results_for", { search: this.search }) : this.$t("no_income_data");
    },
  },
  methods: {
    toOrder(order) {
      const amount = orderTotal(order);
      const currency = order.restaurant?.settings?.price || this.restaurantCurrency;
      return {
        source: order,
        id: order.id,
        phone: order.phone,
        state: menuState(order.state),
        idText: String(order.id),
        time: this.formatOrderTime(order),
        displayName: `${this.toLatin(order.name || "")} ${this.toLatin(order.lastname || "")}`.trim(),
        amount,
        currency,
        amountText: `${amount.toFixed(2)} ${currency}`,
      };
    },
    partsOf(orders) {
      const buckets = {};
      orders.forEach((order) => {
        buckets[order.currency] = (buckets[order.currency] || 0) + order.amount;
      });
      return Object.entries(buckets).map(([currency, amount]) => ({ currency, amount }));
    },
    partsText(parts) {
      return parts.length ? parts.map((part) => `${part.amount.toFixed(2)} ${part.currency}`).join(" + ") : `0.00 ${this.restaurantCurrency}`;
    },
    pluralOrders(count) {
      return `${this.$t("orders")}: ${count}`;
    },
    badgeStyle(state) {
      const colors = this.getStatusColors(state);
      return { backgroundColor: colors.bg, color: colors.fg };
    },
    dotStyle(state) {
      return { backgroundColor: this.getStatusColors(state).fg };
    },
    dayTitle(date) {
      const day = moment(date, "YYYY-MM-DD");
      const weekday = day.toDate().toLocaleDateString(this.$i18n.locale === "en" ? "en" : "sr-Latn", { weekday: "short" });
      return `${weekday.charAt(0).toUpperCase()}${weekday.slice(1)}, ${day.format("DD.MM.YYYY.")}`;
    },
    isOpen(day) {
      const state = this.openDays[day.date];
      return state === undefined ? this.dayRows.length === 1 : state;
    },
    toggle(day) {
      this.$set(this.openDays, day.date, !this.isOpen(day));
    },
    open(order, event) {
      this.$emit("open-order", { order: order.source, guest: order.source, event });
    },
  },
};
</script>
